/**
 * InboundPanel — change-delivery picker (webhook/poll), webhook endpoint +
 * signing secret + verify state + test + recent events, or poll interval.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../../theme';
import { Copy, Refresh } from '../../../icons';
import { IconButton, Select } from '../../../components';
import { useBridge } from '../../../store/bridgeStore';

const POLL_OPTIONS = [
  { value: '1', label: 'Every 1 min' },
  { value: '5', label: 'Every 5 min' },
  { value: '15', label: 'Every 15 min' },
  { value: '60', label: 'Every hour' },
];

export default function InboundPanel({ adapterId, vendorName }: { adapterId: string; vendorName: string }) {
  const { state, actions } = useBridge();
  const isWebhook = state.changeDelivery === 'webhook';
  const w = state.webhookCfg[adapterId] || {};
  const slug = adapterId.replace(/[^a-z0-9]/gi, '').slice(0, 8);
  const url = 'https://hooks.skbridge.io/v1/' + adapterId + '/in';
  const secret = w.secret || 'whsec_' + slug + 'k29fa7be10c4d8e3';
  const events = w.events || [];

  return (
    <View style={{ gap: 12 }}>
      <Text style={styles.q}>How does {vendorName} deliver changes?</Text>

      <Radio label="Webhook" sub={vendorName + ' pushes events as they happen.'} on={isWebhook} onPress={() => actions.setChangeDelivery('webhook')} />
      <Radio label="Poll" sub="Bridge fetches on an interval." on={!isWebhook} onPress={() => actions.setChangeDelivery('poll')} />

      {isWebhook ? (
        <View style={styles.whBox}>
          <Text style={styles.whHeading}>Incoming webhook endpoint</Text>
          <Text style={styles.whDesc}>Give this URL to {vendorName}. SK Bridge accepts and verifies inbound events here.</Text>
          <View style={styles.row}>
            <TextInput value={url} editable={false} style={[styles.input, { flex: 1 }]} />
            <IconButton size={34} title="Copy URL" onPress={() => actions.copyText(url, 'Webhook URL copied')}><Copy size={14} color={colors.ink2} /></IconButton>
          </View>
          <Text style={styles.fieldLabel}>Signing secret</Text>
          <View style={styles.row}>
            <TextInput value={secret} editable={false} style={[styles.input, { flex: 1 }]} />
            <IconButton size={34} title="Rotate secret" onPress={() => actions.regenSecret(adapterId)}><Refresh size={14} color={colors.ink2} /></IconButton>
          </View>
          <View style={styles.verifyRow}>
            <View style={styles.verify}>
              <View style={[styles.verifyDot, { backgroundColor: w.verified ? colors.success : colors.ink3 } as any]} />
              <Text style={[styles.verifyText, { color: w.verified ? colors.success : colors.ink3 } as any]}>{w.verified ? 'Endpoint verified' : 'Awaiting first event'}</Text>
            </View>
            <Pressable onPress={() => actions.testWebhook(adapterId)} style={styles.testBtn}><Text style={styles.testText}>Send test event</Text></Pressable>
          </View>
          {events.length > 0 && (
            <View style={styles.eventsBox}>
              <Text style={styles.eventsHeading}>Recent inbound</Text>
              <View style={{ gap: 4 }}>
                {events.map((ev: any, i: number) => (
                  <View key={i} style={styles.eventRow}>
                    <View style={styles.eventDot} />
                    <Text className="t-mono" style={[styles.eventType, { flex: 1 }]}>{ev.type}</Text>
                    <Text style={styles.eventAgo}>{ev.ago}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}
        </View>
      ) : (
        <View style={styles.pollBox}>
          <Text style={styles.fieldLabel}>Poll interval</Text>
          <Select value={state.pollInterval} options={POLL_OPTIONS} onChange={v => actions.setPollInterval(v as any)} variant="field" height={36} />
        </View>
      )}

      <View style={styles.infoBox}>
        <Text style={styles.infoText}>Sets the adapter's inbound flag for fan-out. This choice is invisible to consumers downstream.</Text>
      </View>
    </View>
  );
}

function Radio({ label, sub, on, onPress }: { label: string; sub: string; on: boolean; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={[styles.radio, { borderColor: on ? colors.accent : colors.line } as any]}>
      <View style={styles.radioCircle}>{on && <View style={styles.radioDot} />}</View>
      <View>
        <Text style={styles.radioLabel}>{label}</Text>
        <Text style={styles.radioSub}>{sub}</Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  q: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium },
  radio: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, padding: 12, borderRadius: radii.r2, borderWidth: 1, backgroundColor: colors.paper2, cursor: 'pointer' } as any,
  radioCircle: { width: 16, height: 16, borderRadius: 8, borderWidth: 1, borderColor: colors.lineStrong, marginTop: 1, alignItems: 'center', justifyContent: 'center' },
  radioDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.accent },
  radioLabel: { fontFamily: fontFamily.sans, fontWeight: fontWeight.medium, color: colors.ink, fontSize: fontSize.md },
  radioSub: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 2 },
  whBox: { borderWidth: 1, borderColor: colors.accentLine, borderRadius: radii.r3, backgroundColor: colors.accentSoft, padding: 13 },
  whHeading: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.accentInk, fontWeight: fontWeight.semibold, marginBottom: 8 },
  whDesc: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink2, lineHeight: 18, marginBottom: 8 },
  row: { flexDirection: 'row', gap: 6, marginBottom: 10 },
  input: { height: 34, paddingHorizontal: 10, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: radii.r2, fontFamily: fontFamily.mono, fontSize: 11, color: colors.ink as any, outlineStyle: 'none' } as any,
  fieldLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, marginBottom: 5 },
  verifyRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  verify: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  verifyDot: { width: 7, height: 7, borderRadius: 4 },
  verifyText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.medium },
  testBtn: { height: 28, paddingHorizontal: 11, borderRadius: radii.r2, borderWidth: 1, borderColor: colors.lineStrong, backgroundColor: colors.card, justifyContent: 'center', cursor: 'pointer' } as any,
  testText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2 },
  eventsBox: { marginTop: 11, borderTopWidth: 1, borderTopColor: colors.accentLine, paddingTop: 9 },
  eventsHeading: { fontFamily: fontFamily.sans, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.ink3, fontWeight: fontWeight.semibold, marginBottom: 6 },
  eventRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  eventDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.success },
  eventType: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink2 },
  eventAgo: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  pollBox: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, backgroundColor: colors.paper2, padding: 13 },
  infoBox: { padding: 10, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.infoLine, backgroundColor: colors.infoSoft, borderRadius: radii.r2 },
  infoText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.info, lineHeight: 18 },
});
